import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SalesDetailPage from './SalesDetailPage';
import {
  EVENTS_ATTENDANCE_COLUMNS,
  SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
} from '../constants/eventsAttendanceColumnsConfig';
import { buildEventsAttendanceFilterGroups } from '../constants/eventsAttendanceFiltersConfig';

const ATTENDANCE_RATE_COLORS = {
  green: 'var(--sl-text-success, #027a48)',
  yellow: 'var(--sl-text-warning, #b54708)',
  red: 'var(--sl-text-error, #b42318)',
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_name && activeFilters.venue_name.length > 0) {
    params.venue_name = activeFilters.venue_name;
  }
  if (activeFilters.attendance_rate) {
    if (activeFilters.attendance_rate.min != null) params.attendance_rate_min = activeFilters.attendance_rate.min;
    if (activeFilters.attendance_rate.max != null) params.attendance_rate_max = activeFilters.attendance_rate.max;
  }
  return params;
};

const buildKpiCards = (current, prior) => [
  {
    label: 'Total Events',
    value: (current.total_events || 0).toLocaleString(),
    current: current.total_events || 0,
    prior: prior.total_events || 0,
  },
  {
    label: 'Total Tickets Sold',
    value: (current.total_sold || 0).toLocaleString(),
    current: current.total_sold || 0,
    prior: prior.total_sold || 0,
  },
  {
    label: 'Total Checked In',
    value: (current.total_checked_in || 0).toLocaleString(),
    current: current.total_checked_in || 0,
    prior: prior.total_checked_in || 0,
  },
  {
    label: 'Avg Attendance Rate',
    value: `${current.avg_attendance_rate || 0}%`,
    current: current.avg_attendance_rate || 0,
    prior: prior.avg_attendance_rate || 0,
  },
];

const renderCellValue = (col, event, { formatEventDate }) => {
  switch (col.id) {
    case 'event_date':
      return (
        <span className="text-nowrap">
          {event.event_date ? formatEventDate(event.event_date) : '\u2014'}
        </span>
      );
    case 'event_name':
      return event.event_name || '\u2014';
    case 'venue_name':
      return event.venue_name || '\u2014';
    case 'capacity':
      return event.capacity > 0 ? event.capacity.toLocaleString() : '\u2014';
    case 'tickets_sold':
      return (event.tickets_sold || 0).toLocaleString();
    case 'checked_in':
      return (event.checked_in || 0).toLocaleString();
    case 'attendance_rate': {
      const rate = event.attendance_rate || 0;
      let color = ATTENDANCE_RATE_COLORS.red;
      if (rate >= 80) color = ATTENDANCE_RATE_COLORS.green;
      else if (rate >= 50) color = ATTENDANCE_RATE_COLORS.yellow;
      return <span style={{ color, fontWeight: 500 }}>{rate}%</span>;
    }
    case 'no_shows':
      return (event.no_shows || 0).toLocaleString();
    case 'comp_tickets':
      return (event.comp_tickets || 0).toLocaleString();
    case 'paid_tickets':
      return (event.paid_tickets || 0).toLocaleString();
    default:
      return '\u2014';
  }
};

const ATTENDANCE_CONFIG = {
  pageTitle: 'Attendance',
  pageSubtitle: 'Event attendance and check-in metrics.',
  sidebarKey: 'events_attendance',
  defaultPreset: 'year_to_date',
  dateModeOptions: [{ key: 'event_date', label: 'Event Date' }],
  defaultSortRules: [{ field: 'event_date', direction: 'desc' }],

  columns: EVENTS_ATTENDANCE_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingEventsAttendanceColumns_${teamId}`,
  firstColumnId: 'event_date',

  kpiFromDetailResponse: true,
  buildKpiCards,
  kpiGridColumns: 4,

  overTimeKey: null,
  salesDataKey: null,
  venueSeriesKey: null,
  topCharts: [],
  groupByOptions: null,
  groupByDefault: null,

  detailEndpoint: (teamId) =>
    `/teams/${teamId}/reporting/dashboard/events/attendance/data.json`,
  detailResponseKey: 'events',
  exportBasePath: (teamId) =>
    `/teams/${teamId}/reporting/dashboard/events/attendance/export`,
  salesExportSection: 'events_attendance',
  detailErrorMessage: 'Failed to load attendance data.',
  detailTableTitle: 'Attendance',
  emptyMessage: 'No attendance data found.',
  searchPlaceholder: 'Search events...',
  paginationLabel: 'events',
  itemLabel: (count) => `event${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,
  renderCellValue,

  filterTitle: 'Filter Attendance',
  filterSubtitle: 'Apply filters to narrow attendance data.',
};

function ReportingEventsAttendancePage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team?.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/dashboard/events/attendance/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => {});
  }, [team?.id, csrfToken]);

  const configWithFilters = {
    ...ATTENDANCE_CONFIG,
    buildFilterGroups: () => buildEventsAttendanceFilterGroups(filterOptions),
  };

  return (
    <SalesDetailPage
      {...props}
      team={team}
      csrfToken={csrfToken}
      config={configWithFilters}
    />
  );
}

export default ReportingEventsAttendancePage;
